"use client";
import React, { useEffect, useId, useRef, useState } from "react";
import { styles } from "./FeaturesWithImage.style";
import { SectionBadge } from "../SectionBadge";
import { SectionBadgeVariants } from "../SectionBadge/SectionBadge.style";
import { Icon } from "../../elements/Icon";
import { arrowDown } from "../../elements/Icon/Icon.types";
import { FeatureItem, FeatureItemProps } from "../../elements/FeatureItem";
import { cx } from "../../../styled-system/css";
import { Button } from "../../elements/Button";
import { ButtonVariants } from "../../shared/Button/Button.types";

const VISIBLE_FEATURES_COUNT = 3;

export type FeaturesWithImageHeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export interface FeaturesWithImageProps {
  badgeText: string;
  headline: string;
  features: FeatureItemProps[];
  buttonLabelExpanded: string;
  buttonLabelCollapsed: string;
  mobileIllustration: React.ReactNode;
  desktopIllustration: React.ReactNode;
  headingLevel?: FeaturesWithImageHeadingLevel;
  defaultExpanded?: boolean;
}

export const FeaturesWithImage = (props: FeaturesWithImageProps) => {
  const {
    badgeText,
    headline,
    features,
    buttonLabelExpanded,
    buttonLabelCollapsed,
    mobileIllustration,
    desktopIllustration,
    headingLevel = "h2",
    defaultExpanded = false,
  } = props;

  const headlineId = useId();
  const hiddenListId = useId();
  const sectionRef = useRef<HTMLElement | null>(null);
  const hiddenListRef = useRef<HTMLLIElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [maxHeight, setMaxHeight] = useState<number>(defaultExpanded ? -1 : 0);

  const visibleFeatures = features.slice(0, VISIBLE_FEATURES_COUNT);
  const hiddenFeatures = features.slice(VISIBLE_FEATURES_COUNT);
  const hasHiddenFeatures = hiddenFeatures.length > 0;
  const actionLabel = isExpanded ? buttonLabelExpanded : buttonLabelCollapsed;

  useEffect(() => {
    const node = hiddenListRef.current;
    if (!node) return;
    setMaxHeight(isExpanded ? node.scrollHeight : 0);
  }, [isExpanded, hiddenFeatures.length]);

  const handleToggle = () => {
    const willCollapse = isExpanded;
    setIsExpanded((prev) => !prev);
    if (willCollapse && sectionRef.current) {
      const prefersReducedMotion =
        typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      sectionRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    }
  };

  return (
    <section ref={sectionRef} className={styles.section()} aria-labelledby={headlineId}>
      <div className={styles.wrapper()}>
        <div className={styles.illustrationMobile()} aria-hidden="true">
          {mobileIllustration}
        </div>
        <div className={styles.textColumn()}>
          <SectionBadge text={badgeText} variant={SectionBadgeVariants.LightGrey} />
          {React.createElement(headingLevel, { id: headlineId, className: styles.headline() }, headline)}
          <div>
            <ul className={styles.featureList()}>
              {visibleFeatures.map((feature) => (
                <FeatureItem key={feature.title} {...feature} />
              ))}
              {hasHiddenFeatures && (
                <li
                  id={hiddenListId}
                  ref={hiddenListRef}
                  className={cx(styles.hiddenList())}
                  aria-hidden={!isExpanded}
                  style={{ maxHeight: maxHeight < 0 ? "none" : `${maxHeight}px` }}
                >
                  <ul className={cx(styles.featureList(), styles.hiddenFeatureList())}>
                    {hiddenFeatures.map((feature) => (
                      <FeatureItem key={feature.title} {...feature} />
                    ))}
                  </ul>
                </li>
              )}
            </ul>
            {hasHiddenFeatures && (
              <Button
                type="button"
                onClick={handleToggle}
                ariaExpanded={isExpanded}
                ariaControls={hiddenListId}
                variant={ButtonVariants.Outlined}
                className={styles.action()}
              >
                {actionLabel}
                <div className={cx(styles.chevron(), isExpanded && styles.chevronExpanded())} aria-hidden="true">
                  <Icon icon={arrowDown} size={24} />
                </div>
              </Button>
            )}
          </div>
        </div>
        <div className={styles.illustrationDesktop()} aria-hidden="true">
          {desktopIllustration}
        </div>
      </div>
    </section>
  );
};

FeaturesWithImage.displayName = "FeaturesWithImage";
